// @ts-nocheck -- RARE-264 mechanical rename; type this file under RARE-265
import "react-app-polyfill/stable";
import "core-js";
import "leaflet-draw/dist/leaflet.draw.css";
import { ApolloClient, ApolloProvider, createHttpLink, from, InMemoryCache } from "@apollo/client";
import { LogtoProvider } from "@logto/react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import App from "./App";
import { logtoConfig } from "./config/logto";
import { LogtoAccessTokenRegistrar } from "./graphql/access-token-bridge";
import { createAuthLink } from "./graphql/auth-link";
import { createErrorLink } from "./graphql/error-link";
import store from "./redux/store";
import reportWebVitals from "./reportWebVitals";
import { parseFeatureFlags } from "./tools/feature-flag";
import "./i18n";
import * as Sentry from "@sentry/react";
import { tryInitFaro } from "./telemetry/faro-init";
import getEnv from "./tools/get-env";

parseFeatureFlags();

// Reload once on a stale-chunk error. Triggers when a React.lazy
// import resolves to a chunk URL that's no longer served — most
// commonly seen in dev mode after webpack-dev-server reissues chunk
// hashes during HMR, but also possible in prod after a deploy if a
// long-lived tab tries to lazy-import a chunk that the new build no
// longer emits. Without this, the user sees a permanent
// `ChunkLoadError` screen with no recovery path. The
// `__chunkReloadAttempted` flag prevents infinite reload loops if
// the chunk genuinely cannot be loaded (e.g. taken offline).
if (typeof window !== "undefined") {
  const reloadOnce = (label) => {
    if (sessionStorage.getItem("__chunkReloadAttempted") === "1") {
      // Already reloaded once and the chunk is still missing —
      // surface the error rather than loop.
      // biome-ignore lint/suspicious/noConsole: deliberate diagnostic when reload recovery has already been attempted
      console.error(`${label}: reload already attempted; not reloading again`);
      return;
    }
    sessionStorage.setItem("__chunkReloadAttempted", "1");
    window.location.reload();
  };
  window.addEventListener("error", (event) => {
    const msg = event?.error?.message || event?.message || "";
    if (msg.includes("Loading chunk") || event?.error?.name === "ChunkLoadError") {
      reloadOnce("ChunkLoadError");
    }
  });
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event?.reason;
    const msg = reason?.message || "";
    if (msg.includes("Loading chunk") || reason?.name === "ChunkLoadError") {
      reloadOnce("ChunkLoadError (rejection)");
    }
  });
  // Clear the guard on a successful navigation. The first chunk
  // load after this point counts as the system working again.
  window.addEventListener("load", () => {
    sessionStorage.removeItem("__chunkReloadAttempted");
  });
}

const env = getEnv("NODE_ENV");

if (env === "production") {
  Sentry.init({
    dsn: "https://3bd16610b557486cf39bfe2626047987@o4509089598210048.ingest.de.sentry.io/4509089599520848",
    integrations: [
      // browserTracingIntegration removed: Faro handles browser tracing.
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
      Sentry.feedbackIntegration({
        // Auto-fill email from Sentry.setUser() and hide the name field
        useSentryUser: { email: "email", name: "fullName" },
        showName: true,
        showEmail: true,
        isEmailRequired: true,
        isNameRequired: true,
        // Single textarea — relabel it to match the prompt
        messageLabel: "What were you doing when this happened?",
        messagePlaceholder: "Briefly describe what you were doing...",
        formTitle: "Report a Bug",
        buttonLabel: "Report a bug",
        submitButtonLabel: "Send report",
      }),
    ],
    replaysOnErrorSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    // tracesSampleRate removed: Sentry browser tracing is disabled.
  });
}

// Faro (Grafana frontend observability) — fires and forgets so that a
// missing or stale chunk never takes the app down. tryInitFaro swallows
// any ChunkLoadError before it reaches the reload handler above.
void tryInitFaro();

const httpLink = createHttpLink({
  uri: `/graphql`,
  // Send the monitor refresh cookie alongside every request so the
  // refresh / logout mutations can read and clear it. Same-origin
  // deploy: 'same-origin' is enough; cross-origin would need 'include'.
  credentials: "same-origin",
});

// The 401 recovery path lives in graphql/error-link.js so it can be
// tested without booting the app. See the module header for the
// state-machine contract it upholds.
//
// `getClient` is a thunk because `client` below is built *from* this
// link. Apollo does not expose the client on the operation context, so
// the refresh mutation has to be handed one explicitly.
const errorLink = createErrorLink({ getClient: () => client });

// Auth link: read the monitor JWT from sessionStorage / memory.
//
// Lives in graphql/auth-link.js so it can be unit-tested and so the
// credential-minting exemption is stated in one place. That exemption
// is not a tidiness concern: the gateway rejects an expired bearer
// with a 401 before routing, so attaching one to the refresh mutation
// makes mid-session recovery impossible. See the module header there.
const authLink = createAuthLink();

const client = new ApolloClient({
  link: from([authLink, errorLink, httpLink]),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          // DataAnalytics has no id, so it's cached as a single embedded
          // object under Query. Without a merge function, each response
          // (e.g. TotalSalesByCategory) replaces the whole object and wipes
          // out sibling fields already cached from other analytics queries
          // (e.g. TotalSalesBySKU), forcing them to refetch even though
          // their own query variables never changed.
          DataAnalytics: {
            merge: true,
          },
        },
      },
    },
  }),
});

createRoot(document.getElementById("root")).render(
  <LogtoProvider config={logtoConfig}>
    <LogtoAccessTokenRegistrar />
    <ApolloProvider client={client}>
      <Provider store={store}>
        <App />
      </Provider>
    </ApolloProvider>
  </LogtoProvider>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
